import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { AlertTriangle, Camera, Car, CheckCircle2, Clock, DollarSign, FileText, PenLine } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { supabase } from "@/integrations/supabase/client"
import { createSignedFileUrl, PHOTO_BUCKET } from "@/lib/vehicleStorage"
import { criteriosData } from "@/lib/criteriosData"
import { SignaturePad } from "@/components/SignaturePad"

const CONSIGNACAO_MARKER = "[weauto:termo-consignacao]"

const LABEL_CATEGORIA: Record<string, string> = {
  Frontal: "Frente",
  Traseira: "Traseira",
  "Lateral Direita": "Lat. Direita",
  "Lateral Esquerda": "Lat. Esquerda",
  "Painel e Instrumentos": "Painel / km",
  Interior: "Interior",
  Motor: "Motor",
  "Detalhes Extras": "Extras",
}

interface PropostaPublicaData {
  criado_em: string
  loja_nome: string
  modalidade: "compra" | "consignacao"
  valor_fipe: number | null
  custo_reparos: number | null
  valor_liquido_sugerido: number | null
  margem_lucro: number | null
  valor_ofertado: number | null
  observacoes: string | null
  observacoes_avaliacao: string | null
  cliente_nome: string | null
  cliente_documento: string | null
  cliente_assinatura: string | null
  cliente_assinado_em: string | null
  veiculo: {
    placa: string
    marca: string
    modelo: string
    ano: number
    anoModelo: string | null
    cor: string | null
    combustivel: string | null
    cambio: string | null
    quilometragem: number | null
    motor: string | null
    portas: number | null
    foto_principal: string | null
  }
  criterios: Array<{
    criterio_id: string
    status: string
    observacoes: string | null
    custo_reparo: number | null
  }>
  fotos: Array<{
    categoria: string
    url: string
    descricao: string | null
  }>
}

interface FotoResolvida {
  categoria: string
  url: string
  descricao: string | null
}

const formatCurrency = (value?: number | null) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0)

const obterNomeCriterio = (criterioId: string) => {
  const criterio = criteriosData.find((item) => item.id === criterioId.trim().toLowerCase())
  return {
    nome: criterio?.nome || criterioId,
    categoria: criterio?.categoria || criterioId.split("_")[0],
  }
}

const parseConsignacao = (observacoes?: string | null) => {
  if (!observacoes?.includes(CONSIGNACAO_MARKER)) return null

  const getValue = (key: string) => {
    const line = observacoes.split("\n").find((item) => item.startsWith(`${key}=`))
    return line?.replace(`${key}=`, "").replace(/\\n/g, "\n") ?? ""
  }

  return {
    valorAnunciado: getValue("valor_anunciado"),
    comissaoLoja: getValue("comissao_loja"),
    prazoConsignacao: getValue("prazo_consignacao"),
    condicoesConsignacao: getValue("condicoes"),
    observacoesAvaliacao: getValue("observacoes_avaliacao"),
  }
}

export default function PropostaPublica() {
  const { token } = useParams<{ token: string }>()
  const [dados, setDados] = useState<PropostaPublicaData | null>(null)
  const [fotosResolvidas, setFotosResolvidas] = useState<FotoResolvida[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [clienteNome, setClienteNome] = useState("")
  const [clienteDocumento, setClienteDocumento] = useState("")
  const [assinaturaUrl, setAssinaturaUrl] = useState("")
  const [assinando, setAssinando] = useState(false)
  const [assinado, setAssinado] = useState(false)

  useEffect(() => {
    if (token) void fetchProposta(token)
  }, [token])

  const fetchProposta = async (t: string) => {
    try {
      const { data, error } = await supabase.rpc("get_proposta_publica", { p_token: t })

      if (error || !data) {
        setNotFound(true)
        return
      }

      const proposta = data as PropostaPublicaData
      setDados(proposta)
      if (proposta.cliente_assinado_em) {
        setAssinado(true)
        setClienteNome(proposta.cliente_nome ?? "")
        setClienteDocumento(proposta.cliente_documento ?? "")
        setAssinaturaUrl(proposta.cliente_assinatura ?? "")
      }

      const resolvidas = await Promise.all(
        proposta.fotos.map(async (foto) => ({
          ...foto,
          url: (await createSignedFileUrl(PHOTO_BUCKET, foto.url)) ?? foto.url,
        }))
      )
      setFotosResolvidas(resolvidas)
    } catch {
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }

  const handleAssinar = async () => {
    if (!token || !clienteNome.trim() || !clienteDocumento.trim() || !assinaturaUrl) return

    setAssinando(true)
    try {
      const { data, error } = await supabase.rpc("assinar_proposta_publica", {
        p_token: token,
        p_nome: clienteNome.trim(),
        p_documento: clienteDocumento.trim(),
        p_assinatura: assinaturaUrl,
      })
      if (error || !(data as { ok?: boolean } | null)?.ok) throw new Error("erro_assinatura")

      setAssinado(true)
      setDados((prev) => prev
        ? {
            ...prev,
            cliente_nome: clienteNome.trim(),
            cliente_documento: clienteDocumento.trim(),
            cliente_assinatura: assinaturaUrl,
            cliente_assinado_em: new Date().toISOString(),
          }
        : prev
      )
    } catch {
      // User can retry from the same form.
    } finally {
      setAssinando(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    )
  }

  if (notFound || !dados) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <AlertTriangle className="h-12 w-12 text-muted-foreground opacity-40" />
        <h1 className="text-lg font-semibold">Link inválido ou expirado</h1>
        <p className="max-w-xs text-sm text-muted-foreground">
          Este link não corresponde a nenhuma proposta ou consignação compartilhada.
        </p>
      </div>
    )
  }

  const { veiculo } = dados
  const isConsignacao = dados.modalidade === "consignacao"
  const consignacao = parseConsignacao(dados.observacoes)
  const reparos = dados.criterios.filter((criterio) => criterio.status === "reprovado")
  const atencao = dados.criterios.filter((criterio) => criterio.status === "atencao")
  const fotosGerais = fotosResolvidas.filter((foto) =>
    foto.descricao?.toLowerCase().includes("principal") && !foto.categoria.startsWith("criterio_")
  )
  const dataDocumento = new Date(dados.criado_em).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
  const titulo = isConsignacao ? "Termo de Consignação" : "Proposta de Compra"

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b bg-white px-4 py-4 text-center shadow-sm">
        <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {titulo}
        </p>
        <h1 className="text-lg font-bold text-foreground">{dados.loja_nome}</h1>
        <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>{dataDocumento}</span>
        </div>
      </div>

      <div className="mx-auto max-w-2xl space-y-5 px-4 py-5">
        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <Car className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-lg font-bold tracking-widest text-foreground">
                  {veiculo.placa}
                </span>
                <Badge variant="secondary">{isConsignacao ? "Consignação" : "Compra"}</Badge>
              </div>
              <p className="mt-0.5 text-sm font-medium text-foreground">
                {veiculo.marca} {veiculo.modelo}
              </p>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <span>{veiculo.ano}{veiculo.anoModelo && veiculo.anoModelo !== String(veiculo.ano) ? `/${veiculo.anoModelo}` : ""}</span>
                {veiculo.quilometragem != null && <span>{veiculo.quilometragem.toLocaleString("pt-BR")} km</span>}
                {veiculo.cor && <span>{veiculo.cor}</span>}
                {veiculo.combustivel && <span>{veiculo.combustivel}</span>}
              </div>
            </div>
          </div>
        </div>

        <section className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            {isConsignacao ? <FileText className="h-4 w-4 text-primary" /> : <DollarSign className="h-4 w-4 text-primary" />}
            <h2 className="text-sm font-semibold text-foreground">
              {isConsignacao ? "Condições da consignação" : "Resumo da proposta"}
            </h2>
          </div>

          {isConsignacao ? (
            <div className="grid gap-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Valor anunciado</span>
                <span className="font-semibold">{formatCurrency(dados.valor_ofertado)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Comissão da loja</span>
                <span className="font-semibold">{consignacao?.comissaoLoja || "Não informado"}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Prazo</span>
                <span className="font-semibold">{consignacao?.prazoConsignacao || "Não informado"}</span>
              </div>
              {consignacao?.condicoesConsignacao && (
                <div className="border-t pt-3">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Condições</p>
                  <p className="whitespace-pre-line text-sm text-foreground">{consignacao.condicoesConsignacao}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="grid gap-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Valor FIPE</span>
                <span className="font-semibold">{formatCurrency(dados.valor_fipe)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Reparos estimados</span>
                <span className="font-semibold text-destructive">- {formatCurrency(dados.custo_reparos)}</span>
              </div>
              <div className="flex items-center justify-between gap-3 border-t pt-3">
                <span className="text-muted-foreground">Valor ofertado</span>
                <span className="text-base font-bold text-primary">{formatCurrency(dados.valor_ofertado)}</span>
              </div>
            </div>
          )}
        </section>

        {fotosGerais.length > 0 && (
          <section>
            <div className="mb-3 flex items-center gap-2">
              <Camera className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold text-foreground">Registro fotográfico</h2>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {fotosGerais.map((foto, index) => (
                <Dialog key={`${foto.categoria}-${index}`}>
                  <DialogTrigger asChild>
                    <div className="group relative aspect-square cursor-pointer overflow-hidden rounded-xl border bg-muted shadow-sm">
                      <img src={foto.url} alt={LABEL_CATEGORIA[foto.categoria] ?? foto.categoria} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent px-2.5 py-2">
                        <p className="text-[11px] font-medium leading-tight text-white">
                          {LABEL_CATEGORIA[foto.categoria] ?? foto.categoria}
                        </p>
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="flex max-w-lg flex-col items-center gap-2 p-4">
                    <p className="text-sm font-medium">{LABEL_CATEGORIA[foto.categoria] ?? foto.categoria}</p>
                    <img src={foto.url} alt={LABEL_CATEGORIA[foto.categoria] ?? foto.categoria} className="w-full rounded-lg object-contain" style={{ maxHeight: "75vh" }} />
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          </section>
        )}

        {(reparos.length > 0 || atencao.length > 0) && (
          <section className="space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h2 className="text-sm font-semibold text-foreground">Pontos da avaliação</h2>
            </div>
            {[...reparos, ...atencao].map((criterio, index) => {
              const { nome, categoria } = obterNomeCriterio(criterio.criterio_id)
              const isReprovado = criterio.status === "reprovado"
              return (
                <div key={`${criterio.criterio_id}-${index}`} className="rounded-xl border bg-white p-3 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{nome}</p>
                      <p className="text-xs text-muted-foreground">{categoria}</p>
                    </div>
                    <Badge variant={isReprovado ? "destructive" : "outline"} className={isReprovado ? "" : "border-amber-300 bg-amber-50 text-amber-700"}>
                      {isReprovado ? (isConsignacao ? "Documentado" : formatCurrency(criterio.custo_reparo)) : "Atenção"}
                    </Badge>
                  </div>
                  {criterio.observacoes && (
                    <p className="mt-2 whitespace-pre-line text-xs text-muted-foreground">{criterio.observacoes}</p>
                  )}
                </div>
              )
            })}
          </section>
        )}

        {reparos.length === 0 && atencao.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-green-200 bg-green-50 p-6 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <p className="text-sm font-medium text-green-800">Nenhum ponto crítico registrado</p>
            <p className="text-xs text-green-700">A avaliação não possui reparos ou observações críticas vinculadas.</p>
          </div>
        )}

        {(dados.observacoes_avaliacao || (!isConsignacao && dados.observacoes)) && (
          <section className="rounded-2xl border bg-white p-4 shadow-sm">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Observações</p>
            <p className="whitespace-pre-line text-sm text-foreground">
              {dados.observacoes_avaliacao || dados.observacoes}
            </p>
          </section>
        )}

        <section className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <PenLine className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">
              {isConsignacao ? "Ciência do proprietário" : "Ciência da proposta"}
            </h2>
          </div>

          {assinado ? (
            <div className="space-y-3 p-4">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium">Ciência registrada</span>
              </div>
              {(dados.cliente_assinatura || assinaturaUrl) && (
                <img
                  src={dados.cliente_assinatura ?? assinaturaUrl}
                  alt="Assinatura"
                  className="h-[100px] w-full max-w-xs rounded-lg border bg-white object-contain"
                />
              )}
              {(dados.cliente_nome || clienteNome) && (
                <p className="text-sm font-medium text-foreground">{dados.cliente_nome ?? clienteNome}</p>
              )}
              {(dados.cliente_documento || clienteDocumento) && (
                <p className="text-xs text-muted-foreground">{dados.cliente_documento ?? clienteDocumento}</p>
              )}
              {dados.cliente_assinado_em && (
                <p className="text-xs text-muted-foreground">
                  {new Date(dados.cliente_assinado_em).toLocaleString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4 p-4">
              <p className="text-xs text-muted-foreground">
                {isConsignacao
                  ? "Assine abaixo para confirmar ciência das condições de consignação descritas neste documento."
                  : "Assine abaixo para confirmar ciência da proposta de compra apresentada."}
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="cliente-nome" className="text-xs">Nome completo</Label>
                <Input
                  id="cliente-nome"
                  value={clienteNome}
                  onChange={(event) => setClienteNome(event.target.value)}
                  placeholder="Nome de quem assina"
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cliente-documento" className="text-xs">CPF/CNPJ</Label>
                <Input
                  id="cliente-documento"
                  value={clienteDocumento}
                  onChange={(event) => setClienteDocumento(event.target.value)}
                  placeholder="Documento"
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Assinatura</Label>
                <SignaturePad value={assinaturaUrl} onChange={setAssinaturaUrl} />
              </div>
              <Button
                className="w-full"
                onClick={handleAssinar}
                disabled={assinando || !clienteNome.trim() || !clienteDocumento.trim() || !assinaturaUrl}
              >
                {assinando
                  ? <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                  : <PenLine className="mr-2 h-4 w-4" />
                }
                Confirmar ciência
              </Button>
            </div>
          )}
        </section>

        <div className="rounded-2xl border bg-white p-4 text-center shadow-sm">
          <p className="text-xs text-muted-foreground">
            Documento compartilhado por <strong>{dados.loja_nome}</strong>
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground/70">{dataDocumento}</p>
        </div>
      </div>
    </div>
  )
}
